import { Flex, HStack, Text, useToast, UseToastOptions } from '@chakra-ui/react'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useMangoApi } from '~/atoms/MangoApi'
import { useModalState } from '~/atoms/ModalState'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useGetExtensions } from '~/queries/extensionQueries'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Spacing } from '~/types/ThemeTypes'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

import { ExtensionInstance } from '@mangovoice/types'

const MoveFaxModalParts = [
  'footerContainer',
  'phoneNameText',
  'phoneNumberText',
] as const

const helpers = createMultiStyleConfigHelpers(MoveFaxModalParts)
export const MoveFaxModalStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      alignItems: 'stretch',
      gap: Spacing._2,
    },
    toInput: {
      height: '3.5rem',
    },
    phoneNameText: {
      fontWeight: '900',
      noOfLines: 1,
    },
    phoneNumberText: {
      flexShrink: 0,
    },

    footerContainer: {
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '0.62rem',
    },
  },
})

export const useMoveFaxModalStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig(
    getName({ MoveFaxModalStyle }),
    props,
  ) as MultiStyleType<typeof MoveFaxModalParts>
}

export const MoveFaxModal = () => {
  const toast = useToast()
  const {
    moveFaxModal: [moveFaxModalState, setMoveFaxModalState],
  } = useModalState()
  const [selectedExtensionID, setSelectedExtensionID] = useState(0)
  const mangoApi = useMangoApi()

  const { results: VirtualFaxMachines, fetchNextPage } = useGetExtensions({
    type: 'VirtualFaxMachine',
  })
  const location = useLocation()
  const navigate = useNavigate()
  const faxId = location.pathname.split('/')[2]

  const styles = useMoveFaxModalStyle()

  const faxesJsx = useCallback((faxMachines: ExtensionInstance[]) => {
    return (
      faxMachines?.map((extension) => {
        return {
          value: extension.id,
          content: (
            <HStack key={extension.id} p="0.5rem">
              <Text sx={styles.phoneNameText}>{extension.name}</Text>
              <Text sx={styles.phoneNumberText}>{`${extension.number}`}</Text>
            </HStack>
          ),
        }
      }) ?? []
    )
  }, [])

  const selectedExtensionIndex = useMemo(() => {
    return VirtualFaxMachines?.findIndex(
      (fax) => fax.id === selectedExtensionID,
    )
  }, [VirtualFaxMachines, selectedExtensionID])

  function closeModal() {
    setMoveFaxModalState({ open: false })
  }

  async function moveFax() {
    if (!faxId || !selectedExtensionID) {
      return
    }

    const [moveFax, markFax] = await Promise.all([
      mangoApi.moveFaxToVirtualFax(Number(faxId), {
        extension_id: selectedExtensionID,
      }),
      mangoApi.markFaxAsUnread(Number(faxId)),
    ])

    if (moveFax.ok && markFax.ok) {
      closeModal()

      if (location.pathname.includes(ApplicationRoutes.FAX)) {
        // set query params to empty string
        navigate(ApplicationRoutes.FAX) // Resets the page in the "No Fax Selected" state
      }

      return
    }

    const errorMessages = ['An error occurred while moving the fax.']
    const status: UseToastOptions['status'] = !moveFax.ok ? 'error' : 'warning'

    if (!markFax.ok) {
      errorMessages.push('Fax was not marked as unread.')
    }

    if (!moveFax.ok) {
      errorMessages.push('Failed to move the fax to the selected virtual fax.')
    }

    toast({
      title: 'Error Moving Fax',
      description: errorMessages.join(' '),
      status,
      duration: 5000,
      isClosable: true,
    })
  }

  return (
    <MangoModal
      isOpen={moveFaxModalState.open}
      onClose={closeModal}
      header="Move Fax"
      body={
        <Flex flexDirection="column" gap="3">
          <DropdownSelect
            placeholder="Select Virtual Fax..."
            items={faxesJsx(VirtualFaxMachines)}
            selectedIndex={selectedExtensionIndex}
            onScrollEndReached={fetchNextPage}
            onSelect={setSelectedExtensionID}
          />

          <Flex p="2">
            <Text>
              If you don't have access to the selected Virtual Fax you won't be
              able to view this fax once it's been moved.
            </Text>
          </Flex>
        </Flex>
      }
      footer={
        <Flex sx={styles.footerContainer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={closeModal}
          >
            Cancel
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={moveFax}
          >
            Move Fax
          </MangoButton>
        </Flex>
      }
    ></MangoModal>
  )
}
